/**
 * SqliteRepo — SQLite implementation of {@link Repo} for the Capacitor native
 * build (durable storage in the app sandbox). Runs on any {@link SqlExecutor}:
 * a Capacitor executor on device, a sql.js executor in tests. Cache tables keep
 * the full row as JSON plus the columns we query by; the optimistic overlay
 * reuses the shared assembleAnswerDetail, so this adapter is behaviourally
 * identical to the Dexie one (enforced by the shared conformance suite).
 */
import type {
  Inspection,
  QuestionAnswer,
  QuestionAspectResponse,
  InspectionHumanRankResponse,
  PhotoComparison,
  NegativeObservation,
  Rectification,
  Rank,
  DeficiencyFindingWithCodes,
  DeficiencyFindingListItem,
} from "@/types/api";
import type { Repo } from "../repo";
import { assembleAnswerDetail, synthesizeQueuedNoms, withInspectionMeta } from "../overlay";
import type { OutboxMutation, OutboxAttachment, OutboxState, PullDelta, AnswerDetail } from "../types";
import type { SqlExecutor } from "./sql-executor";

const ACTIVE: OutboxState[] = ["pending", "syncing", "failed"];

const DDL = `
CREATE TABLE IF NOT EXISTS inspections (id INTEGER PRIMARY KEY, data TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS answers (id INTEGER PRIMARY KEY, inspectionId INTEGER, questionId INTEGER, data TEXT NOT NULL);
CREATE INDEX IF NOT EXISTS answers_insq ON answers (inspectionId, questionId);
CREATE INDEX IF NOT EXISTS answers_ins ON answers (inspectionId);
CREATE TABLE IF NOT EXISTS aspects (id INTEGER PRIMARY KEY, answerId INTEGER, aspect TEXT, data TEXT NOT NULL);
CREATE INDEX IF NOT EXISTS aspects_ans ON aspects (answerId);
CREATE TABLE IF NOT EXISTS humanRank (id INTEGER PRIMARY KEY, answerId INTEGER, rankId INTEGER, data TEXT NOT NULL);
CREATE INDEX IF NOT EXISTS human_ans ON humanRank (answerId);
CREATE TABLE IF NOT EXISTS photo (id INTEGER PRIMARY KEY, answerId INTEGER, data TEXT NOT NULL);
CREATE INDEX IF NOT EXISTS photo_ans ON photo (answerId);
CREATE TABLE IF NOT EXISTS noms (id INTEGER PRIMARY KEY, inspectionId INTEGER, data TEXT NOT NULL);
CREATE INDEX IF NOT EXISTS noms_ins ON noms (inspectionId);
CREATE TABLE IF NOT EXISTS rectifications (id INTEGER PRIMARY KEY, negativeObservationId INTEGER, data TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS ranks (id INTEGER PRIMARY KEY, data TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS outbox (
  id INTEGER PRIMARY KEY AUTOINCREMENT, entity TEXT, coalesceKey TEXT, state TEXT,
  inspectionId INTEGER, questionId INTEGER, aspect TEXT, rankId INTEGER, negativeObservationId INTEGER,
  clientId TEXT, clientUpdatedAt TEXT, seq INTEGER, error TEXT, createdAt TEXT, payload TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS outbox_coalesce ON outbox (coalesceKey);
CREATE INDEX IF NOT EXISTS outbox_state ON outbox (state);
CREATE INDEX IF NOT EXISTS outbox_insq ON outbox (inspectionId, questionId);
CREATE TABLE IF NOT EXISTS attachments (
  id INTEGER PRIMARY KEY AUTOINCREMENT, state TEXT, clientId TEXT, ownerKind TEXT, inspectionId INTEGER,
  questionId INTEGER, aspect TEXT, ownerClientId TEXT, ownerId INTEGER, attachmentOwnerType TEXT, category TEXT, transcript TEXT,
  filename TEXT, mime TEXT, blobB64 TEXT, error TEXT, createdAt TEXT
);
CREATE TABLE IF NOT EXISTS kv (key TEXT PRIMARY KEY, value TEXT);
CREATE TABLE IF NOT EXISTS meta (key TEXT PRIMARY KEY, value TEXT);
CREATE TABLE IF NOT EXISTS assets (id INTEGER PRIMARY KEY, mime TEXT, blobB64 TEXT NOT NULL);
`;

const CURSOR_KEY = "cursor";
const SEQ_KEY = "seq";

async function blobToB64(b: Blob): Promise<string> {
  const buf = new Uint8Array(await b.arrayBuffer());
  let bin = "";
  for (const byte of buf) bin += String.fromCharCode(byte);
  return typeof btoa !== "undefined" ? btoa(bin) : Buffer.from(buf).toString("base64");
}
function b64ToBlob(s: string, mime: string): Blob {
  const bin = typeof atob !== "undefined" ? atob(s) : Buffer.from(s, "base64").toString("binary");
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

const qs = (n: number) => Array.from({ length: n }, () => "?").join(",");

export class SqliteRepo implements Repo {
  private sql: SqlExecutor;
  private ready: Promise<void>;
  constructor(executor: SqlExecutor) {
    this.sql = executor;
    this.ready = this.init();
  }
  private async init(): Promise<void> {
    await this.sql.exec(DDL);
    // Add columns introduced after the initial schema — no-op (throws
    // "duplicate column") on DBs that already have them, so swallow it.
    try { await this.sql.exec("ALTER TABLE attachments ADD COLUMN ownerId INTEGER"); } catch { /* already present */ }
    try { await this.sql.exec("ALTER TABLE attachments ADD COLUMN attachmentOwnerType TEXT"); } catch { /* already present */ }
  }
  private async db(): Promise<SqlExecutor> { await this.ready; return this.sql; }

  /* ── cache reads ─────────────────────────────────────────────── */
  private async one<T>(table: string, where: string, params: unknown[]): Promise<T | undefined> {
    const db = await this.db();
    const rows = await db.query<{ data: string }>(`SELECT data FROM ${table} WHERE ${where} LIMIT 1`, params);
    return rows[0] ? (JSON.parse(rows[0].data) as T) : undefined;
  }
  private async many<T>(table: string, where = "1", params: unknown[] = []): Promise<T[]> {
    const db = await this.db();
    const rows = await db.query<{ data: string }>(`SELECT data FROM ${table} WHERE ${where}`, params);
    return rows.map((r) => JSON.parse(r.data) as T);
  }

  getInspection(id: number) { return this.one<Inspection>("inspections", "id = ?", [id]); }
  listInspections() { return this.many<Inspection>("inspections"); }
  listAnswers(inspectionId: number) { return this.many<QuestionAnswer>("answers", "inspectionId = ?", [inspectionId]); }
  listNegativeObservations(inspectionId: number) { return this.many<NegativeObservation>("noms", "inspectionId = ?", [inspectionId]); }
  listAllNegativeObservations() { return this.many<NegativeObservation>("noms"); }
  async listQueuedNegativeObservations(inspectionId: number): Promise<NegativeObservation[]> {
    const queued = (await this.listOutbox(ACTIVE)).filter((m) => m.entity === "nom");
    if (queued.length === 0) return [];
    const answers = await this.listAnswers(inspectionId);
    const envByQ = new Map(answers.map((a) => [a.questionId, a.id]));
    return synthesizeQueuedNoms(queued, (_insp, q) => envByQ.get(q), inspectionId);
  }
  async listAllQueuedNegativeObservations(): Promise<NegativeObservation[]> {
    const queued = (await this.listOutbox(ACTIVE)).filter((m) => m.entity === "nom");
    if (queued.length === 0) return [];
    const answers = await this.many<QuestionAnswer>("answers");
    const envByKey = new Map(answers.map((a) => [`${a.inspectionId}:${a.questionId}`, a.id]));
    const synth = synthesizeQueuedNoms(queued, (insp, q) => envByKey.get(`${insp}:${q}`));
    // Stamp vessel/type from the cached inspection so the global Observations
    // list groups/filters/displays the offline NOM like a synced one.
    const inspById = new Map((await this.many<Inspection>("inspections")).map((i) => [i.id, i]));
    return synth.map((n) => withInspectionMeta(n, inspById.get(n.inspectionId)));
  }

  async listAllQueuedFindings(): Promise<DeficiencyFindingListItem[]> {
    const db = await this.db();
    const rows = await db.query<{ value: string }>("SELECT value FROM kv WHERE key LIKE 'findings:%'");
    if (rows.length === 0) return [];
    const inspById = new Map((await this.many<Inspection>("inspections")).map((i) => [i.id, i]));
    const out: DeficiencyFindingListItem[] = [];
    for (const r of rows) {
      let list: DeficiencyFindingWithCodes[] = [];
      try { list = (JSON.parse(r.value) as DeficiencyFindingWithCodes[]) ?? []; } catch { /* skip malformed */ }
      for (const fwc of list) {
        // Only offline-created (temp negative id) findings.
        if (!fwc?.finding || fwc.finding.id >= 0) continue;
        const insp = inspById.get(fwc.finding.inspectionId);
        out.push({
          ...fwc,
          inspectionType: insp?.type ?? null,
          inspectionName: insp?.name ?? null,
          vesselId: insp?.vesselId ?? null,
          vesselName: null,
          vesselImo: null,
        });
      }
    }
    return out;
  }
  async evictQueuedFinding(tempId: number): Promise<void> {
    const db = await this.db();
    const rows = await db.query<{ key: string; value: string }>("SELECT key, value FROM kv WHERE key LIKE 'findings:%'");
    for (const r of rows) {
      let list: DeficiencyFindingWithCodes[] = [];
      try { list = (JSON.parse(r.value) as DeficiencyFindingWithCodes[]) ?? []; } catch { continue; }
      const next = list.filter((fwc) => fwc?.finding?.id !== tempId);
      if (next.length !== list.length) await db.run("UPDATE kv SET value=? WHERE key=?", [JSON.stringify(next), r.key]);
    }
    await db.run("DELETE FROM kv WHERE key IN (?,?)", [`deficiency-finding:${tempId}`, `rectifications:by-finding:${tempId}`]);
  }
  listRectifications() { return this.many<Rectification>("rectifications"); }
  getRanks() { return this.many<Rank>("ranks"); }

  async getAnswerDetail(inspectionId: number, questionId: number): Promise<AnswerDetail> {
    const cachedEnvelope = (await this.one<QuestionAnswer>("answers", "inspectionId = ? AND questionId = ?", [inspectionId, questionId])) ?? null;
    const answerId = cachedEnvelope?.id ?? null;
    const [cachedAspects, cachedHuman, cachedPhoto] = await Promise.all([
      answerId != null ? this.many<QuestionAspectResponse>("aspects", "answerId = ?", [answerId]) : Promise.resolve([]),
      answerId != null ? this.many<InspectionHumanRankResponse>("humanRank", "answerId = ?", [answerId]) : Promise.resolve([]),
      answerId != null ? this.many<PhotoComparison>("photo", "answerId = ?", [answerId]) : Promise.resolve([]),
    ]);
    const pending = await this.listOutboxFor(inspectionId, questionId);
    return assembleAnswerDetail({ inspectionId, questionId, answerId, cachedEnvelope, cachedAspects, cachedHuman, cachedPhoto, pending });
  }

  /* ── cache writes ────────────────────────────────────────────── */
  private async upsert(table: string, cols: string[], rows: Array<Record<string, unknown>>, full: unknown[]): Promise<void> {
    const db = await this.db();
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i]!;
      const allCols = [...cols, "data"];
      const vals = [...cols.map((c) => r[c] ?? null), JSON.stringify(full[i])];
      const setClause = allCols.filter((c) => c !== "id").map((c) => `${c}=excluded.${c}`).join(", ");
      await db.run(`INSERT INTO ${table} (${allCols.join(",")}) VALUES (${qs(allCols.length)}) ON CONFLICT(id) DO UPDATE SET ${setClause}`, vals);
    }
  }

  async applyDelta(d: PullDelta): Promise<void> {
    const db = await this.db();
    await db.tx(async () => {
      if (d.inspections?.length) await this.upsert("inspections", ["id"], d.inspections as never, d.inspections);
      if (d.answers?.length) await this.upsert("answers", ["id", "inspectionId", "questionId"], d.answers as never, d.answers);
      if (d.aspects?.length) await this.upsert("aspects", ["id", "answerId", "aspect"], d.aspects as never, d.aspects);
      if (d.humanRankResponses?.length) await this.upsert("humanRank", ["id", "answerId", "rankId"], d.humanRankResponses as never, d.humanRankResponses);
      if (d.photoComparisons?.length) await this.upsert("photo", ["id", "answerId"], d.photoComparisons as never, d.photoComparisons);
      if (d.negativeObservations?.length) await this.upsert("noms", ["id", "inspectionId"], d.negativeObservations as never, d.negativeObservations);
      if (d.rectifications?.length) await this.upsert("rectifications", ["id", "negativeObservationId"], d.rectifications as never, d.rectifications);
      if (d.ranks?.length) await this.upsert("ranks", ["id"], d.ranks as unknown as Record<string, unknown>[], d.ranks);
    });
    // Tombstones: evict deleted rows from the mirror + kv cache (no-op if the
    // id was never cached). Pull is otherwise upsert-only.
    for (const del of d.deletions ?? []) {
      if (del.entity === "inspection") {
        await this.evictInspection(del.entityId);
        await db.run("DELETE FROM kv WHERE key IN (?,?,?,?,?)", [
          `inspection-basic:${del.entityId}`, `inspection-class:${del.entityId}`,
          `inspection-noms:${del.entityId}`, `inspection-opennoms:${del.entityId}`,
          `inspection-response-attachments:${del.entityId}`,
        ]);
      } else if (del.entity === "negativeObservation") {
        await db.run("DELETE FROM noms WHERE id = ?", [del.entityId]);
        await db.run("DELETE FROM rectifications WHERE negativeObservationId = ?", [del.entityId]);
        await db.run("DELETE FROM kv WHERE key IN (?,?)", [`observation:${del.entityId}`, `nom-root-causes:${del.entityId}`]);
      } else if (del.entity === "rectification") {
        await db.run("DELETE FROM rectifications WHERE id = ?", [del.entityId]);
      }
    }
  }

  async evictInspection(inspectionId: number): Promise<void> {
    const db = await this.db();
    const ans = await db.query<{ id: number }>("SELECT id FROM answers WHERE inspectionId = ?", [inspectionId]);
    const noms = await db.query<{ id: number }>("SELECT id FROM noms WHERE inspectionId = ?", [inspectionId]);
    await db.tx(async () => {
      await db.run("DELETE FROM inspections WHERE id = ?", [inspectionId]);
      await db.run("DELETE FROM answers WHERE inspectionId = ?", [inspectionId]);
      await db.run("DELETE FROM noms WHERE inspectionId = ?", [inspectionId]);
      for (const n of noms) {
        await db.run("DELETE FROM rectifications WHERE negativeObservationId = ?", [n.id]);
      }
      for (const a of ans) {
        await db.run("DELETE FROM aspects WHERE answerId = ?", [a.id]);
        await db.run("DELETE FROM humanRank WHERE answerId = ?", [a.id]);
        await db.run("DELETE FROM photo WHERE answerId = ?", [a.id]);
      }
    });
  }

  async remapInspectionId(tempId: number, serverId: number): Promise<void> {
    const db = await this.db();
    const from = String(tempId);
    const to = String(serverId);
    await db.tx(async () => {
      const rows = await db.query<Record<string, unknown>>("SELECT * FROM outbox WHERE inspectionId = ?", [tempId]);
      for (const r of rows) {
        const m = this.rowToMutation(r);
        const payload = { ...m.payload };
        if (payload.inspectionId === tempId) payload.inspectionId = serverId;
        // tempId is a unique negative integer → safe verbatim substring swap.
        const coalesceKey = m.coalesceKey.split(from).join(to);
        await db.run("UPDATE outbox SET inspectionId=?, coalesceKey=?, payload=? WHERE id=?", [serverId, coalesceKey, JSON.stringify(payload), m.id]);
      }
      await db.run("UPDATE attachments SET inspectionId=? WHERE inspectionId=?", [serverId, tempId]);
    });
  }

  async remapNegativeObservationId(tempId: number, serverId: number): Promise<void> {
    const db = await this.db();
    const from = String(tempId);
    const to = String(serverId);
    await db.tx(async () => {
      const rows = await db.query<Record<string, unknown>>("SELECT * FROM outbox WHERE negativeObservationId = ?", [tempId]);
      for (const r of rows) {
        const m = this.rowToMutation(r);
        const payload = { ...m.payload };
        if (payload.negativeObservationId === tempId) payload.negativeObservationId = serverId;
        const coalesceKey = m.coalesceKey.split(from).join(to);
        await db.run("UPDATE outbox SET negativeObservationId=?, coalesceKey=?, payload=? WHERE id=?", [serverId, coalesceKey, JSON.stringify(payload), m.id]);
      }
      // Also remap any queued (generic) attachment whose ownerId is this temp NOM
      // (observation evidence uploaded offline before the NOM synced), else it
      // stays stuck on "Owner not yet synced" forever (mirrors remapFindingId).
      await db.run("UPDATE attachments SET ownerId=? WHERE ownerId=? AND attachmentOwnerType='negative_observation'", [serverId, tempId]);
    });
  }
  async remapFindingId(tempId: number, serverId: number): Promise<void> {
    const db = await this.db();
    const from = String(tempId);
    const to = String(serverId);
    // The outbox has no deficiencyFindingId column here — match by PAYLOAD.
    await db.tx(async () => {
      const rows = await db.query<Record<string, unknown>>("SELECT * FROM outbox", []);
      for (const r of rows) {
        const m = this.rowToMutation(r);
        const payload = { ...m.payload } as Record<string, unknown>;
        let hit = false;
        if (payload.deficiencyFindingId === tempId) { payload.deficiencyFindingId = serverId; hit = true; }
        if (payload.findingId === tempId) { payload.findingId = serverId; hit = true; }
        if (hit) {
          const coalesceKey = m.coalesceKey.split(from).join(to);
          await db.run("UPDATE outbox SET coalesceKey=?, payload=? WHERE id=?", [coalesceKey, JSON.stringify(payload), m.id]);
        }
      }
      await db.run("UPDATE attachments SET ownerId=? WHERE ownerId=?", [serverId, tempId]);
    });
  }

  /* ── outbox ──────────────────────────────────────────────────── */
  private rowToMutation(r: Record<string, unknown>): OutboxMutation {
    return {
      id: r.id as number, entity: r.entity as OutboxMutation["entity"], coalesceKey: r.coalesceKey as string,
      inspectionId: (r.inspectionId as number) ?? undefined, questionId: (r.questionId as number) ?? undefined,
      aspect: (r.aspect as OutboxMutation["aspect"]) ?? undefined, rankId: (r.rankId as number) ?? undefined,
      negativeObservationId: (r.negativeObservationId as number) ?? undefined,
      payload: JSON.parse((r.payload as string) ?? "{}"), clientId: r.clientId as string,
      clientUpdatedAt: r.clientUpdatedAt as string, seq: r.seq as number,
      state: r.state as OutboxState, error: (r.error as string) ?? null, createdAt: r.createdAt as string,
    };
  }
  private async listOutboxFor(inspectionId: number, questionId: number): Promise<OutboxMutation[]> {
    const db = await this.db();
    const rows = await db.query("SELECT * FROM outbox WHERE inspectionId = ? AND questionId = ?", [inspectionId, questionId]);
    return rows.map((r) => this.rowToMutation(r));
  }

  async enqueue(m: Omit<OutboxMutation, "id" | "state" | "createdAt" | "seq"> & { seq?: number }): Promise<void> {
    const db = await this.db();
    await db.tx(async () => {
      const existing = await db.query<{ id: number }>(`SELECT id FROM outbox WHERE coalesceKey = ? AND state IN (${qs(ACTIVE.length)}) LIMIT 1`, [m.coalesceKey, ...ACTIVE]);
      const seq = m.seq ?? (await this.bumpSeq());
      const payload = JSON.stringify(m.payload);
      if (existing[0]) {
        await db.run("UPDATE outbox SET payload=?, clientUpdatedAt=?, seq=?, state='pending', error=NULL WHERE id=?", [payload, m.clientUpdatedAt, seq, existing[0].id]);
      } else {
        await db.run(
          "INSERT INTO outbox (entity,coalesceKey,state,inspectionId,questionId,aspect,rankId,negativeObservationId,clientId,clientUpdatedAt,seq,error,createdAt,payload) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?)",
          [m.entity, m.coalesceKey, "pending", m.inspectionId ?? null, m.questionId ?? null, m.aspect ?? null, m.rankId ?? null, m.negativeObservationId ?? null, m.clientId, m.clientUpdatedAt, seq, null, new Date().toISOString(), payload],
        );
      }
    });
  }

  async listOutbox(states: OutboxState[] = ACTIVE): Promise<OutboxMutation[]> {
    const db = await this.db();
    const rows = await db.query(`SELECT * FROM outbox WHERE state IN (${qs(states.length)}) ORDER BY seq`, states);
    return rows.map((r) => this.rowToMutation(r));
  }
  async markOutbox(ids: number[], state: OutboxState, error: string | null = null): Promise<void> {
    if (!ids.length) return;
    const db = await this.db();
    await db.run(`UPDATE outbox SET state=?, error=? WHERE id IN (${qs(ids.length)})`, [state, error, ...ids]);
  }
  async deleteOutbox(ids: number[]): Promise<void> {
    if (!ids.length) return;
    const db = await this.db();
    await db.run(`DELETE FROM outbox WHERE id IN (${qs(ids.length)})`, ids);
  }
  async hasPending(coalesceKey: string): Promise<boolean> {
    const db = await this.db();
    const rows = await db.query<{ n: number }>(`SELECT COUNT(*) AS n FROM outbox WHERE coalesceKey = ? AND state IN (${qs(ACTIVE.length)})`, [coalesceKey, ...ACTIVE]);
    return (rows[0]?.n ?? 0) > 0;
  }

  /* ── attachments ─────────────────────────────────────────────── */
  async enqueueAttachment(a: Omit<OutboxAttachment, "id" | "state" | "createdAt">): Promise<void> {
    const db = await this.db();
    const b64 = await blobToB64(a.blob);
    await db.run(
      "INSERT INTO attachments (state,clientId,ownerKind,inspectionId,questionId,aspect,ownerClientId,ownerId,attachmentOwnerType,category,transcript,filename,mime,blobB64,error,createdAt) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)",
      ["pending", a.clientId, a.ownerKind, a.inspectionId, a.questionId ?? null, a.aspect ?? null, a.ownerClientId ?? null, a.ownerId ?? null, a.attachmentOwnerType ?? null, a.category ?? null, a.transcript ?? null, a.filename, a.mime, b64, null, new Date().toISOString()],
    );
  }
  async listAttachments(states: OutboxState[] = ACTIVE): Promise<OutboxAttachment[]> {
    const db = await this.db();
    const rows = await db.query<Record<string, unknown>>(`SELECT * FROM attachments WHERE state IN (${qs(states.length)})`, states);
    return rows.map((r) => ({
      id: r.id as number, clientId: r.clientId as string, ownerKind: r.ownerKind as OutboxAttachment["ownerKind"],
      inspectionId: r.inspectionId as number, questionId: (r.questionId as number) ?? null, aspect: (r.aspect as OutboxAttachment["aspect"]) ?? null,
      ownerClientId: (r.ownerClientId as string) ?? null, ownerId: (r.ownerId as number) ?? null, attachmentOwnerType: (r.attachmentOwnerType as string) ?? null, category: (r.category as OutboxAttachment["category"]) ?? null,
      transcript: (r.transcript as string) ?? null, filename: r.filename as string, mime: r.mime as string,
      blob: b64ToBlob(r.blobB64 as string, r.mime as string), state: r.state as OutboxState, error: (r.error as string) ?? null, createdAt: r.createdAt as string,
    }));
  }
  async markAttachment(id: number, state: OutboxState, error: string | null = null): Promise<void> {
    const db = await this.db();
    await db.run("UPDATE attachments SET state=?, error=? WHERE id=?", [state, error, id]);
  }
  async deleteAttachment(id: number): Promise<void> {
    const db = await this.db();
    await db.run("DELETE FROM attachments WHERE id=?", [id]);
  }

  /* ── response cache + meta ───────────────────────────────────── */
  async kvGet<T>(key: string): Promise<T | undefined> {
    const db = await this.db();
    const rows = await db.query<{ value: string }>("SELECT value FROM kv WHERE key=? LIMIT 1", [key]);
    return rows[0] ? (JSON.parse(rows[0].value) as T) : undefined;
  }
  async kvPut(key: string, value: unknown): Promise<void> {
    const db = await this.db();
    await db.run("INSERT INTO kv (key,value) VALUES (?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value", [key, JSON.stringify(value)]);
  }
  async kvPutMany(entries: Array<{ key: string; value: unknown }>): Promise<void> {
    if (!entries.length) return;
    const db = await this.db();
    // One transaction → one commit/fsync for all rows (vs one per kvPut).
    await db.tx(async () => {
      for (const { key, value } of entries) {
        await db.run("INSERT INTO kv (key,value) VALUES (?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value", [key, JSON.stringify(value)]);
      }
    });
  }
  async kvDeleteByPrefix(prefix: string): Promise<void> {
    const db = await this.db();
    // Escape LIKE wildcards so a `%`/`_`/`\` in the prefix matches literally.
    const escaped = prefix.replace(/[\\%_]/g, (c) => `\\${c}`);
    await db.run("DELETE FROM kv WHERE key LIKE ? ESCAPE '\\'", [`${escaped}%`]);
  }

  /* ── binary asset cache ──────────────────────────────────────── */
  async putAsset(attachmentId: number, blob: Blob): Promise<void> {
    const db = await this.db();
    const b64 = await blobToB64(blob);
    await db.run("INSERT INTO assets (id,mime,blobB64) VALUES (?,?,?) ON CONFLICT(id) DO UPDATE SET mime=excluded.mime, blobB64=excluded.blobB64", [attachmentId, blob.type, b64]);
  }
  async getAsset(attachmentId: number): Promise<Blob | undefined> {
    const db = await this.db();
    const rows = await db.query<{ mime: string; blobB64: string }>("SELECT mime, blobB64 FROM assets WHERE id=? LIMIT 1", [attachmentId]);
    return rows[0] ? b64ToBlob(rows[0].blobB64, rows[0].mime) : undefined;
  }

  async deleteCachedAttachment(kvKey: string, attachmentId: number): Promise<void> {
    // ONE transaction: prune the row from the per-owner list kv AND drop the
    // cached bytes — an offline delete can't leave an orphaned blob behind.
    const db = await this.db();
    await db.tx(async () => {
      const rows = await db.query<{ value: string }>("SELECT value FROM kv WHERE key=? LIMIT 1", [kvKey]);
      if (rows[0]) {
        try {
          const list = JSON.parse(rows[0].value) as Array<{ id?: number }>;
          if (Array.isArray(list)) {
            const next = list.filter((a) => a?.id !== attachmentId);
            if (next.length !== list.length) {
              await db.run("INSERT INTO kv (key,value) VALUES (?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value", [kvKey, JSON.stringify(next)]);
            }
          }
        } catch { /* malformed kv — leave it untouched */ }
      }
      await db.run("DELETE FROM assets WHERE id=?", [attachmentId]);
    });
  }

  async getCursor(): Promise<string | null> {
    const db = await this.db();
    const rows = await db.query<{ value: string }>("SELECT value FROM meta WHERE key=? LIMIT 1", [CURSOR_KEY]);
    return rows[0]?.value ?? null;
  }
  async setCursor(iso: string): Promise<void> {
    const db = await this.db();
    await db.run("INSERT INTO meta (key,value) VALUES (?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value", [CURSOR_KEY, iso]);
  }
  private async bumpSeq(): Promise<number> {
    const db = await this.db();
    const rows = await db.query<{ value: string }>("SELECT value FROM meta WHERE key=? LIMIT 1", [SEQ_KEY]);
    const next = (rows[0] ? Number(rows[0].value) : 0) + 1;
    await db.run("INSERT INTO meta (key,value) VALUES (?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value", [SEQ_KEY, String(next)]);
    return next;
  }
  async nextSeq(): Promise<number> { return this.bumpSeq(); }

  async pendingCount(): Promise<number> {
    const db = await this.db();
    const o = await db.query<{ n: number }>(`SELECT COUNT(*) AS n FROM outbox WHERE state IN ('pending','failed')`);
    const a = await db.query<{ n: number }>(`SELECT COUNT(*) AS n FROM attachments WHERE state IN ('pending','failed')`);
    return (o[0]?.n ?? 0) + (a[0]?.n ?? 0);
  }

  async clear(): Promise<void> {
    const db = await this.db();
    await db.tx(async () => {
      for (const t of ["inspections", "answers", "aspects", "humanRank", "photo", "noms", "rectifications", "ranks", "outbox", "attachments", "kv", "meta", "assets"]) {
        await db.run(`DELETE FROM ${t}`);
      }
    });
  }
}
